import React from 'react';
import { TrendingUp, DollarSign, BarChart2, Globe } from 'lucide-react';

/**
 * Компонент секции параметров расчета ROI
 */
const RoiParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  revenueImpactFactors,
  costReductionFactors,
  regionFactors
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
        Параметры расчета эффективности
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Существующие затраты на одного студента */}
        <div>
          <label htmlFor="existingCostsPerStudent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-blue-500" />
            Текущие затраты на студента
          </label>
          <input
            type="text"
            id="existingCostsPerStudent"
            name="existingCostsPerStudent"
            value={formData.existingCostsPerStudent}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.existingCostsPerStudent ? 'input-error' : ''}`}
            placeholder="Например: 500"
          />
          {errors.existingCostsPerStudent && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.existingCostsPerStudent}
            </p>
          )}
        </div>

        {/* Ожидаемый прирост эффективности */}
        <div>
          <label htmlFor="expectedEfficiencyGain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <BarChart2 className="w-4 h-4 mr-1 text-blue-500" />
            Ожидаемый прирост эффективности (%)
          </label>
          <input
            type="text"
            id="expectedEfficiencyGain"
            name="expectedEfficiencyGain"
            value={formData.expectedEfficiencyGain}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.expectedEfficiencyGain ? 'input-error' : ''}`}
            placeholder="Например: 20"
          />
          {errors.expectedEfficiencyGain && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.expectedEfficiencyGain}
            </p>
          )}
        </div>

        {/* Влияние на доходы */}
        <div>
          <label htmlFor="revenueImpact" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Влияние на доходы
          </label>
          <select
            id="revenueImpact"
            name="revenueImpact"
            value={formData.revenueImpact}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.revenueImpact ? 'input-error' : ''}`}
          >
            {revenueImpactFactors.map((factor) => (
              <option key={factor.id} value={factor.id}>
                {factor.name}
              </option>
            ))}
          </select>
          {errors.revenueImpact && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.revenueImpact}
            </p>
          )}
        </div>

        {/* Фактор снижения затрат */}
        <div>
          <label htmlFor="costReductionFactor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Фактор снижения затрат
          </label>
          <select
            id="costReductionFactor"
            name="costReductionFactor"
            value={formData.costReductionFactor}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.costReductionFactor ? 'input-error' : ''}`}
          >
            {costReductionFactors.map((factor) => (
              <option key={factor.id} value={factor.id}>
                {factor.name}
              </option>
            ))}
          </select>
          {errors.costReductionFactor && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.costReductionFactor}
            </p>
          )}
        </div>

        {/* Регион внедрения */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Globe className="w-4 h-4 mr-1 text-blue-500" />
            Регион внедрения
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.region ? 'input-error' : ''}`}
          >
            {regionFactors.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.region}
            </p>
          )}
        </div>

        {/* Валюта (отображается, но недоступна для редактирования) */}
        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Валюта расчета
          </label>
          <input
            type="text"
            id="currency"
            name="currency"
            value={formData.currency}
            disabled
            className="form-input w-full bg-gray-100 dark:bg-gray-600"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Определяется автоматически на основе региона
          </p>
        </div>

        {/* Пояснения по параметрам эффективности */}
        <div className="col-span-full bg-white dark:bg-gray-700/50 rounded-lg p-3 mt-2 border border-blue-100 dark:border-blue-900/50">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Как рассчитывается ROI</h4>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Расчет возврата инвестиций (ROI) основан на сравнении затрат на внедрение и обслуживание системы
            с экономическим эффектом от внедрения ИИ. Экономический эффект складывается из прямой экономии затрат 
            (более эффективное использование ресурсов) и дополнительных доходов (рост качества образования, 
            привлечение новых студентов, расширение программ).
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoiParametersSection;